"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Active, Cancelled" },
  { from: "Active", to: "Paused, Cancelled" },
  { from: "Paused", to: "Active, Cancelled" },
  {
    from: "Completed",
    to: "The job sets this when the last cycle is done. You do not Mark As Completed",
  },
  {
    from: "Cancelled",
    to: "Nothing. Mark Recurring Expense As is hidden. Cancelled is a dead end",
  },
];

const RULES = [
  {
    topic: "This is a schedule, not an expense",
    rule: "Saving a Recurring Expense does not post money. The daily job (or Create Expense retry) creates a real Expense, and that expense posts immediately",
  },
  {
    topic: "Subscription gate",
    rule: "Creating a Recurring Expense is limited by your plan. Retrying a failed cycle (Create Expense) also checks Create and the plan. Editing, Mark As, and deleting are not plan-gated the same way",
  },
  {
    topic: "Save always makes Draft",
    rule: "There is no Save & Active. Draft and Paused are skipped by the daily job",
  },
  {
    topic: "Recurring Expense number",
    rule: "Field is labelled Expenses#. Default prefix RECE under Recurring Expense Configuration. Generated expenses use Expense numbering (EX), not RECE",
  },
  {
    topic: "Division, Branch, Warehouse",
    rule: "Required, plus Repeat Every, Frequency, Start On, Expense Account, Amount, Paid Through. Customer/Vendor Name is required on the form. There is no Profile Name field",
  },
  {
    topic: "No primary-bank gate, no stock",
    rule: "Same as Expenses. Inventory Config is not used here",
  },
  {
    topic: "Generated docs are live expenses",
    rule: "Unlike Recurring Bills (Draft bills) or Recurring Invoices (Draft invoices), Expenses have no draft status — money moves when the expense is created",
  },
  {
    topic: "Edit and delete in the UI: Draft only",
    rule: "Active, Paused, Completed, and Cancelled cannot be edited or deleted on screen. Generated expenses are not deleted with the schedule",
  },
  {
    topic: "Paused does not catch up",
    rule: "Missed dates while Paused are not created when you Mark As Active again",
  },
  {
    topic: "Failed cycle retry",
    rule: "Recurring Activities → Execution Log & Expenses. FAILED → Create Expense from the current template, then SUCCESS",
  },
  {
    topic: "No Duplicate, no Export, no Convert from",
    rule: "Nothing converts into a Recurring Expense",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a recurring expense" },
      { heading: "2. Repeat schedule" },
      { heading: "3. How expenses actually get created" },
      { heading: "4. Viewing, searching, and the list" },
      { heading: "5. Editing a recurring expense" },
      { heading: "6. Changing status" },
      { heading: "7. Recurring Activities — success, failed, retry" },
      { heading: "8. Deleting a recurring expense" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function RecurringExpensesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Recurring Expenses
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who pays the same cost on a fixed repeat — rent from
            Paid Through, a monthly software fee that is not a vendor bill
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Recurring Expense</B> is a schedule. <B>Save</B> makes{" "}
            <B>Draft</B>. <B>Mark As Active</B>, then the daily job creates a
            real <B>Expense</B> — and that expense posts money immediately.
            Prefix is <B>RECE</B>. If you need a payable with items, use{" "}
            <B>Recurring Bills</B> instead.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a recurring expense
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse. Only <B>Save</B>.
          </p>
          <Definitions
            items={[
              {
                term: "Expenses#",
                desc: (
                  <>
                    Recurring Expense number. Default prefix <B>RECE</B> under{" "}
                    <B>Recurring Expense Configuration</B>.
                  </>
                ),
              },
              {
                term: "Customer/Vendor Name",
                desc: <>Required on this form (unlike a one-off Expense).</>,
              },
              {
                term: "Expense Account, Amount, Paid Through",
                desc: (
                  <>
                    Copied onto each generated expense. Same account rules as
                    Expenses.
                  </>
                ),
              },
              {
                term: "Notes, receipts, Terms",
                desc: (
                  <>
                    Upload Your Files (3 × 5 MB). Terms from the configuration
                    drawer. No Profile Name field.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Recurring Expense with Repeat Every, Expense Account, Paid Through, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Repeat schedule
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Same math as Recurring Bills / Recurring Invoices: Day(s) uses
            Start On as a generation day; Week / Month / Year first generate
            on Start On plus frequency. Never Expires or End On → Completed
            when the last cycle is done. Paused does not catch up.
          </p>
          <Screenshot caption="Screenshot: Repeat Every, Frequency, Start On, End On, Never Expires." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. How expenses actually get created
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Daily job, Active schedules only. Each hit creates an Expense
            (EX) that posts ledger immediately — there is no Draft expense
            to Mark As later.
          </p>
          <Callout type="warning">
            Recurring Bills create Draft bills you still open. Recurring
            Expenses create posted spend on the generation day.
          </Callout>
          <Screenshot caption="Screenshot: a generated expense opened from Recurring Activities." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Viewing, searching, and the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Status tabs Draft / Active / Paused / Completed / Cancelled. No
            Export. Preferences → Recurring Expense Configuration. Detail
            shows Customer Name, Repeat Every, and Recurring Activities.
          </p>
          <Screenshot caption="Screenshot: Recurring Expenses list with Mark Recurring Expense As." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Editing a recurring expense
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            UI: Draft only. Location stays locked after save.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Button: <B>Mark Recurring Expense As</B>.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[160px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Mark Recurring Expense As on a Draft." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Recurring Activities — success, failed, retry
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Execution Log &amp; Expenses. FAILED → <B>Create Expense</B>{" "}
            retry. Money effect applies on create and on retry.
          </p>
          <Screenshot caption="Screenshot: Recurring Activities FAILED row with Create Expense." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Deleting a recurring expense
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only on screen. Expenses already generated stay in
            Expenses.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/expenses"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Expenses <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where generated expenses land — already posted
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/recurring-bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Recurring Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Same schedule, but Draft bills you still open
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
